import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  ExternalLink,
  Calendar,
  Mic,
  MapPin,
  Phone,
  Mail,
  Trophy,
  GraduationCap,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";

const CEO_PHOTO =
  "/leadership/dion-magee-portrait.jpg";

const CERTIFICATIONS = [
  "Authorized Training Partner Instructor — PMP",
  "Certified Meeting Professional (CMP)",
  "Certified Hospitality Educator (CHE)",
  "Project Management Professional (PMP)",
  "Certificate of Meeting Management (CMM)",
  "Healthcare Meeting Compliance Certificate (HMCC)",
  "Certified Incentive Specialist (CIS)",
  "Certified Travel Director (CTD)",
  "Pandemic Compliance Advisor (PCA)",
  "Governance, Risk, Compliance Professional (GRCP)",
  "Governance, Risk, Compliance Auditor (GRCA)",
  "Certified Risk, Compliance, Management Professional (CRCMP)",
  "Sustainable Meeting Planning Program (SMPP)",
  "Good Clinical Practices (GCP)",
  "Certified Six Sigma Black Belt (CSSBB)",
  "MBE / SBE — Minority Certified",
];

const AWARDS = [
  {
    year: "2020",
    title: "Project Manager of the Year",
    org: "Project Management Institute — New Jersey",
  },
  {
    year: "2017",
    title: "Digital Magazine Award · Finalist",
    org: "Digital Media Awards (UK)",
  },
  {
    year: "2009",
    title: "Educator of the Year",
    org: "Temple University",
  },
  {
    year: "2005",
    title: "Corporate Planner of the Year",
    org: "Event Solutions Magazine",
  },
  {
    year: "2005",
    title: "Young & Inspiring Event Professional in Industry",
    org: "Special Events Magazine",
  },
];

const BIO_SECTIONS = [
  {
    heading: "An artistic visionary in events, hospitality & global project management",
    body:
      "Sir Dion Magee combines executive leadership, creative talent, and deep community engagement — with certifications across meetings, hospitality, and governance/risk to match. As CEO of Magee Enterprises, Dion leads a consulting and full-service concierge service for corporate and social events, associations, and complex program management engagements.",
  },
  {
    heading: "Educator, author, and industry standard-setter",
    body:
      "An adjunct professor and Certified Hospitality Educator, Dion is passionate about teaching hospitality and event professionals to succeed. He is the author of EventiFocus — an Event Project Management framework that pairs recognized project planning techniques with industry-specific guidance, governance, and practices for meetings and special events. He also founded OmniPear Professional Development & Education Institute, an online platform delivering top-tier hospitality and professional certification courses.",
  },
  {
    heading: "Enterprise-scale results, before Magee Enterprises",
    body:
      "Before founding the firm, Dion spent 14 years at Verizon as Senior Manager of Meetings & Events, transforming corporate programs into award-winning main attractions and designing one of the country's largest recognition and incentive programs for a leading global telecom. His pharmaceutical-industry work is a case study in how planning, discipline, and persistence improve lives every day.",
  },
];

export default function Leadership() {
  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Leadership — Meet Sir Dion Magee, CEO"
        description="Meet the team behind Magee Enterprises. Led by Sir Dion Magee, CEO — a certified project management and event technology expert delivering global meetings, associations, and productions."
        path="/leadership"
        keywords="Sir Dion Magee, Magee Enterprises leadership, event industry CEO, certified event project manager, event technology executive"
      />
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] -top-20 -right-20 opacity-40" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] bottom-0 -left-24 opacity-30" />
        <HexMark
          size={260}
          opacity={0.06}
          className="absolute -left-16 top-32 rotate-[10deg] pointer-events-none hidden md:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <span className="chip">
              <span className="chip-dot" /> Meet the Team
            </span>
            <MaskedReveal
              lines={["Our", "Leadership."]}
              className="mt-6 font-editorial tracking-[-0.03em] leading-[0.95] text-[16vw] md:text-[10vw] lg:text-[8.6vw]"
              stagger={0.1}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                An award-winning, deeply credentialed team — held together by one
                artistic vision and a rigorous project-management discipline.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CEO CARD */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] top-40 -left-40 opacity-35" />
        <div className="brand-bloom brand-bloom-warm w-[380px] h-[380px] -bottom-20 -right-16 opacity-30" />

        {/* 3D orbs decoration */}

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">Chief Executive</span>
              <div className="grid grid-cols-12">
                {/* Photo */}
                <div className="col-span-12 lg:col-span-5 relative aspect-[4/5] lg:aspect-auto lg:min-h-[720px] overflow-hidden bg-gradient-to-br from-[#0B2545] to-[#050A14]">
                  <img
                    src={CEO_PHOTO}
                    alt="Sir Dion Magee, CEO of Magee Enterprises"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/60 via-transparent to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <span className="chip-dot" /> CEO · Founder
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/80 mb-2">
                      Portrait · Magee HQ
                    </p>
                    <p className="font-editorial text-2xl md:text-3xl leading-tight">
                      Plan. Lead.{" "}
                      <span className="text-cyan-300 italic font-medium">
                        Inspire.
                      </span>
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="col-span-12 lg:col-span-7 p-8 md:p-12 lg:p-14">
                  <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
                    Founder &amp; Chief Executive
                  </span>
                  <h2 className="mt-4 font-editorial text-3xl md:text-5xl leading-[1] tracking-[-0.03em]">
                    Award-Winning Sir Dion Magee
                  </h2>
                  <p className="mt-3 font-mono-brand text-[11px] text-[var(--text-muted)] uppercase tracking-[0.14em]">
                    CHE · PMP · CMP · CMM · HMCC · CGSP · GRCP · GRCA · CSSBB ·
                    CRCMP · CTA · CIS · PCA
                  </p>

                  <p className="mt-8 text-[var(--text)] text-lg leading-relaxed">
                    Award-winning meeting, event, and project-management
                    professional. A leading expert in corporate and social
                    events, association programming, and pharmaceutical-grade
                    project delivery — and an adjunct professor and certified
                    hospitality educator.
                  </p>

                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <a
                      href="https://tidycal.com/sirdionmagee/business-general-consultation-30-minutes"
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary justify-center"
                    >
                      <Calendar size={14} /> Connect with me
                    </a>
                    <Link
                      to="/speaker"
                      className="btn-ghost justify-center"
                      data-testid="leadership-speaker-info-hero"
                    >
                      <Mic size={14} /> Speaker Info
                      <ArrowRight size={12} />
                    </Link>
                  </div>

                  {/* Credly badge */}
                  <a
                    href="https://www.credly.com/badges/e6b203e2-5776-422b-b94b-292302186b6f"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-8 inline-flex items-center gap-4 border border-[var(--border)] rounded-2xl p-4 hover:border-[var(--accent)]/40 transition-colors"
                  >
                    <span className="h-14 w-14 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(6,182,212,0.55)]">
                      <Award size={26} strokeWidth={2} />
                    </span>
                    <div>
                      <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                        Verified · Credly
                      </div>
                      <div className="font-editorial text-lg">
                        PMP Authorized Training Partner Instructor
                      </div>
                    </div>
                    <ExternalLink
                      size={14}
                      className="text-[var(--text-muted)] ml-auto"
                    />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BIO NARRATIVE */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 space-y-16 md:space-y-24">
          {BIO_SECTIONS.map((b, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <article className="grid grid-cols-12 gap-6 md:gap-12">
                <div className="col-span-12 md:col-span-5">
                  <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">
                    Chapter · 0{i + 1}
                  </span>
                  <h3 className="mt-4 font-editorial text-3xl md:text-5xl leading-[1] tracking-[-0.025em] text-balance">
                    {b.heading}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CERTIFICATIONS GRID */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <HexMark
          size={240}
          opacity={0.06}
          className="absolute -right-14 top-16 rotate-[24deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <BadgeCheck size={12} /> Current Certifications
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-2xl">
                  Sixteen credentials.{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    One standard.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Every engagement is anchored by verified expertise across meetings,
                hospitality, project management, and governance.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c} delay={i * 0.03}>
                <div className="card-white p-5 flex items-start gap-4 hover:border-[var(--accent)]/40 hover:shadow-[0_10px_30px_-16px_rgba(6,182,212,0.35)] transition-all duration-500">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                    <BadgeCheck size={16} />
                  </div>
                  <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-editorial text-[15px] leading-tight mt-1">
                      {c}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AWARDS TIMELINE */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip chip-warm">
              <Award size={12} /> Recognition
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em]">
              A career of
              <br />
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent italic font-medium">
                measurable impact.
              </span>
            </h2>
          </Reveal>

          <div className="mt-16 relative border-l-2 border-[var(--border)]">
            {AWARDS.map((a, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="relative pl-8 md:pl-14 pb-10">
                  <div className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600 border-2 border-white shadow-[0_4px_12px_rgba(6,182,212,0.4)]" />
                  <div className="flex flex-wrap items-baseline gap-4">
                    <span className="font-editorial numeral-mono text-4xl md:text-5xl !text-[var(--accent)] !bg-none">
                      {a.year}
                    </span>
                    <div>
                      <div className="font-editorial text-xl md:text-2xl leading-tight">
                        {a.title}
                      </div>
                      <div className="text-[var(--text-muted)] mt-1 text-sm md:text-base">
                        {a.org}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OMNIPEAR — Professional Development & Education Institute */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[480px] h-[480px] -top-32 -right-24 opacity-25" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -left-24 opacity-20" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <GraduationCap size={12} /> Education · CPD-Accredited Institute
            </span>
          </Reveal>

          <div className="mt-6 grid grid-cols-12 gap-8 md:gap-12 items-start">
            {/* Left: Logo card */}
            <Reveal className="col-span-12 md:col-span-5">
              <div
                className="relative rounded-3xl bg-white border border-[var(--border)] p-10 md:p-12 flex items-center justify-center overflow-hidden"
                data-testid="omnipear-logo-card"
                style={{
                  boxShadow:
                    "0 20px 50px -25px rgba(11,37,69,0.15), 0 8px 20px -12px rgba(6,182,212,0.10)",
                }}
              >
                <div className="absolute inset-0 hex-pattern opacity-30 pointer-events-none" />
                <div className="absolute -top-16 -left-16 w-[240px] h-[240px] rounded-full bg-cyan-500/12 blur-3xl" />
                <div className="absolute -bottom-16 -right-8 w-[240px] h-[240px] rounded-full bg-sky-500/10 blur-3xl" />
                <span className="corner-cross" style={{ top: 12, left: 12 }} />
                <span className="corner-cross" style={{ bottom: 12, right: 12 }} />
                <img
                  src="/omnipear/logo.webp"
                  alt="OmniPear — Professional Development & Education Institute"
                  className="relative w-full max-w-md h-auto object-contain"
                />
              </div>

              {/* Award badge — real Brandon Hall Group Bronze seal */}
              <div
                className="mt-5 relative rounded-2xl bg-gradient-to-br from-[#0B2545] to-[#0EA5E9] text-white p-5 md:p-6 overflow-hidden"
                style={{ boxShadow: "0 20px 50px -25px rgba(6,182,212,0.4)" }}
                data-testid="omnipear-award-badge"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-cyan-400/25 blur-3xl" />
                <div className="relative flex items-center gap-4">
                  <img
                    src="/omnipear/brandon-hall-bronze.png"
                    alt="Brandon Hall Group Bronze — HCM Excellence Award, Learning & Development"
                    className="w-24 h-24 md:w-28 md:h-28 object-contain shrink-0 drop-shadow-[0_10px_20px_rgba(0,0,0,0.35)]"
                  />
                  <div className="min-w-0">
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-200">
                      Award-Winning
                    </div>
                    <div className="mt-1.5 font-editorial text-[17px] leading-tight">
                      2× Brandon Hall Group Bronze
                    </div>
                    <div className="mt-1 text-[13px] text-white/80 leading-snug">
                      Best Certification Program · Best Custom Content
                    </div>
                    <div className="mt-2 font-mono-brand text-[9.5px] uppercase tracking-[0.2em] text-white/55">
                      HCM Excellence · Learning &amp; Development
                    </div>
                  </div>
                </div>
              </div>
              {/* CTA link to official OmniPear site */}
              <a
                href="https://omnipear.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="omnipear-visit-cta"
                className="mt-4 group flex items-center justify-between rounded-2xl border border-[var(--border)] bg-white px-5 py-4 hover:border-cyan-400/60 hover:shadow-[0_18px_40px_-20px_rgba(6,182,212,0.35)] transition-all"
              >
                <div className="min-w-0">
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                    Visit the institute
                  </div>
                  <div className="mt-1 font-editorial text-[16px] leading-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors">
                    omnipear.com
                  </div>
                </div>
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(6,182,212,0.5)] group-hover:translate-x-0.5 transition-transform shrink-0">
                  <ExternalLink size={16} />
                </span>
              </a>
            </Reveal>
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] text-balance">
                  Dion Magee &{" "}
                  <span className="italic font-medium bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent">
                    OmniPear.
                  </span>
                </h2>
              </Reveal>

              <Reveal delay={0.15}>
                <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                  Dion Magee is founder and{" "}
                  <span className="text-[var(--text)] font-semibold">
                    Chief Learning Officer (CLO)
                  </span>{" "}
                  for OmniPear, a{" "}
                  <span className="text-[var(--text)] font-semibold">
                    CPD-accredited professional development and education
                    institute.
                  </span>{" "}
                  The organization specializes in designing and delivering
                  comprehensive training programs tailored to professional
                  advancement and organizational growth across multiple
                  industries. As a CPD-accredited institution, OmniPear designs
                  programs for professional credits, ensuring that all
                  educational content aligns with the highest industry
                  standards.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="mt-4 text-[var(--text-muted)] text-lg leading-relaxed">
                  OmniPear&rsquo;s Professional Development &amp; Education
                  programs won{" "}
                  <span className="text-[var(--text)] font-semibold">
                    2 coveted Brandon Hall Group Bronze Awards
                  </span>{" "}
                  for &ldquo;Best Certification Program&rdquo; and &ldquo;Best
                  Custom Content&rdquo; categories. The organization caters to
                  professionals seeking to acquire skills and knowledge in
                  various subject areas, including:
                </p>
              </Reveal>

              {/* Subject areas grid */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    title: "Specialized Certifications",
                    body: "CMP · CTD · CGSP and other industry credentials",
                    icon: BadgeCheck,
                    accent: true,
                  },
                  {
                    title: "Hospitality & Tourism",
                    body: "Standards, service, and destination expertise",
                    icon: MapPin,
                  },
                  {
                    title: "Meetings Management",
                    body: "End-to-end planning fundamentals to advanced practice",
                    icon: Calendar,
                  },
                  {
                    title: "Leadership Development",
                    body: "Executive presence, coaching, and team performance",
                    icon: Award,
                  },
                  {
                    title: "Business Entrepreneurship",
                    body: "Formation, positioning, and go-to-market frameworks",
                    icon: GraduationCap,
                  },
                  {
                    title: "Weddings & Special Events",
                    body: "Design, logistics, and vendor coordination at scale",
                    icon: Trophy,
                  },
                ].map((s, i) => (
                  <Reveal key={s.title} delay={0.25 + i * 0.05}>
                    <div
                      data-testid={`omnipear-subject-${i + 1}`}
                      className={`card-white p-4 md:p-5 h-full flex items-start gap-3 ${
                        s.accent ? "sm:col-span-2 grad-border" : ""
                      }`}
                    >
                      <div className="icon-tile shrink-0" style={{ width: 36, height: 36 }}>
                        <s.icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-editorial text-[16px] leading-tight">
                          {s.title}
                        </div>
                        <div className="mt-1 text-[13px] text-[var(--text-muted)] leading-relaxed">
                          {s.body}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* M.E. DMC — Destination Management Company banner */}
      <section className="relative py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Link
            to="/services/me-dmc"
            data-testid="leadership-me-dmc-banner"
            className="group relative block overflow-hidden rounded-3xl border border-[var(--border)] bg-gradient-to-br from-white via-[var(--surface)] to-white px-6 md:px-10 py-8 md:py-10 hover:shadow-[0_25px_60px_-20px_rgba(6,182,212,0.35)] hover:border-cyan-400/40 transition-all duration-500"
          >
            <div className="absolute inset-0 hex-pattern opacity-60 pointer-events-none" />
            <div className="absolute -top-16 -left-16 w-[280px] h-[280px] rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="absolute -bottom-20 -right-16 w-[320px] h-[320px] rounded-full bg-sky-500/12 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="shrink-0 relative">
                <div className="absolute inset-0 rounded-full bg-cyan-400/25 blur-2xl scale-110" />
                <img
                  src="/dmc/logo.png"
                  alt="M.E. DMC — Destination Management Company"
                  className="relative w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_10px_28px_rgba(6,182,212,0.35)]"
                />
              </div>
              <div className="flex-1">
                <span className="chip">
                  <MapPin size={12} /> A division of Magee Enterprises
                </span>
                <h3 className="mt-4 font-editorial text-2xl md:text-3xl lg:text-4xl leading-tight tracking-[-0.02em] text-balance max-w-2xl">
                  M.E. DMC —{" "}
                  <span className="italic font-medium bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent">
                    global reach, local mastery.
                  </span>
                </h3>
                <p className="mt-3 text-[var(--text-muted)] leading-relaxed max-w-2xl text-[14.5px]">
                  Our specialized Destination Management Company arm — local
                  logistics, planning, and coordination for corporate meetings,
                  incentives, and special events.
                </p>
              </div>
              <span className="btn-primary shrink-0 group-hover:translate-x-0.5 transition-transform">
                Explore M.E. DMC <ArrowRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* SPEAKER + CONTACT CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-24 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <Mic size={12} /> Looking for a speaker or facilitator?
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Book Sir Dion for your{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                  next stage.
                </span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <a
                  href="https://tidycal.com/sirdionmagee/business-general-consultation-15-minutes"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                >
                  <Calendar size={14} /> Book a 15-min Consultation
                </a>
                <Link
                  to="/speaker"
                  className="btn-dark border-white/20"
                  data-testid="leadership-speaker-info-cta"
                >
                  <Mic size={14} /> Speaker Info <ArrowRight size={12} />
                </Link>
                <Link to="/contact" className="btn-dark border-white/20">
                  <ArrowRight size={14} /> Or say hello
                </Link>
              </div>

              {/* Contact strip */}
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80 text-sm max-w-3xl mx-auto">
                <a
                  href="tel:+18882992250"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Phone size={14} className="text-cyan-400" />
                  +1 · 888-299-2250
                </a>
                <a
                  href="mailto:dion@mageeenterprises.com"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Mail size={14} className="text-cyan-400" />
                  dion@mageeenterprises.com
                </a>
                <span className="flex items-center gap-3 justify-center">
                  <MapPin size={14} className="text-cyan-400" />
                  Global operations · HQ North America
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
